import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import CommonLayout from 'components/layout/common'
import SearchContent from 'components/search'
import { request } from 'lib/api'
import { ENDPOINTS } from 'lib/api/const'
import { withI18next } from 'lib/i18n/withI18next'
import {
  segmentTrackProductListViewed,
  segmentTrackSearchProducts,
} from 'lib/segment'
import StateStorage from 'lib/state-storage'
import Tracking from 'lib/tracking'
import { getFilterParams } from 'lib/utils/category'
import { isAuthCookiesAvailable } from 'lib/utils/handle-response'
import { PAGE_NAME } from 'pages/const'
import BasePage from '../base'
import { getValidSortParam } from '../category/util'
import {
  SEARCH_FILTER_BRANDS,
  SEARCH_FILTER_CATEGORIES,
  SEARCH_FILTER_COLORS,
  SEARCH_FILTER_MAX_PRICE,
  SEARCH_FILTER_SIZES,
  SEARCH_SORT_OPTIONS,
} from './const'
import styles from './style.scss'
import { getFiltersFromQuery, syncRoutePathForSearch } from './util'

class Search extends BasePage {
  static shouldRunGetInitialProps = true

  static async getInitialProps({
    device,
    isServer,
    query,
    req,
    res,
    segment_anonymous_id: segmentAnonymousID,
    store,
    user_uid: userUID,
    asPath,
  }) {
    const {
      brand: brandQuery,
      category: categoriesQuery,
      color: colorQuery,
      filter_size: sizeQuery,
      price: priceQuery,
      subcategory: categoryQuery,
      zone: shop,
      lang,
      term,
      sort,
    } = query
    const sortQuery = getValidSortParam(sort)
    const filterData = getFiltersFromQuery({
      brandQuery,
      categoriesQuery,
      categoryQuery,
      colorQuery,
      priceQuery,
      sizeQuery,
      store,
      asPath,
    })

    const initialProps = {
      query: term,
      sortQuery,
    }

    if (isServer) {
      BasePage.loadInitialContentFromServer({
        req,
        res,
        store,
      })

      const auth = StateStorage.getCookie('auth')
      const isGuestMode = !isAuthCookiesAvailable(auth)

      try {
        const results = await request({
          url: ENDPOINTS.productsSearch.link,
          params: {
            ...getFilterParams(filterData),
            query: term,
            'filter[order_by]': sortQuery.by || 'popular',
          },
          shop,
          lang,
          isGuestMode,
          userUID,
          segmentAnonymousID,
          device,
          ...(Boolean(auth?.token) && { token: auth.token }),
        })

        return {
          ...initialProps,
          results,
        }
      } catch (e) {
        // TODO: Silently exit
      }
    } else {
      BasePage.loadInitialContentFromClient(req, res, store)
    }

    return initialProps
  }

  onChangeURL = () => {
    const { filter, sort } = this.props
    syncRoutePathForSearch({
      filterData: filter,
      sortData: sort.by,
    })
  }

  track = ({ hits, query, page = 1, total, query_id, query_idx }) => {
    const {
      filter,
      sort: { by: sort_by },
    } = this.props

    segmentTrackSearchProducts({
      active_filters: filter,
      page_number: page,
      productIds: hits.map((hit) => hit.id_product),
      query,
      sort_by,
      query_id,
      query_idx,
    })
    if (!hits.length) {
      segmentTrackProductListViewed(
        { page_number: page, product_list_ids: [] },
        { ref_type: 'zero_search_result', ref_id: '' },
      )
    }

    Tracking.trackEvent(Tracking.EVENT_NAME_SEARCH, {
      'rtb-search': hits.slice(0, 4).map((item) => item.id_product),
      'search-term': query,
      'search-results-count': total,
    })
  }

  getFilterableCategories(data = SEARCH_FILTER_CATEGORIES) {
    if (!data) {
      return []
    }

    const {
      internationalization: { country, language },
    } = this.props
    const filterableCategories = []

    data.map((cat) => {
      if (cat[country] === '') {
        return null
      }

      return filterableCategories.push({
        name: cat[language] || cat.key,
        value: cat.key,
      })
    })

    this.filterableCategories = filterableCategories
    return this.filterableCategories
  }

  getSortAndFilterData({
    colorData = SEARCH_FILTER_COLORS,
    priceData = SEARCH_FILTER_MAX_PRICE,
    sortData = SEARCH_SORT_OPTIONS,
    sizeData = SEARCH_FILTER_SIZES,
    brandData = SEARCH_FILTER_BRANDS,
  } = {}) {
    if (!brandData || !colorData || !priceData || !sortData || !sizeData) {
      return {}
    }

    const {
      internationalization: { country, language },
    } = this.props
    const colors = colorData.map((color) => ({
      name: color[language] || color.key,
      value: color.key,
      reference: color.reference,
    }))
    const sizes = sizeData.map((size) => ({
      name: size[language] || size.key,
      value: size.key,
    }))
    const brands = brandData.map((brand) => ({
      name: brand.key,
      value: brand.key,
    }))
    const price = priceData[country] || priceData.us
    const prices = { name: price, value: price }
    const sort = sortData.map((option) => ({
      name: option.label[language] || option.label.en,
      value: option.key,
    }))
    this.sortAndFilterData = {
      brands,
      colors,
      prices,
      sizes,
      sort,
    }

    return this.sortAndFilterData
  }

  render() {
    const { isPhone, query, results, t, sortQuery } = this.props
    const filterableCategories =
      this.filterableCategories || this.getFilterableCategories()
    const sortAndFilterData =
      this.sortAndFilterData || this.getSortAndFilterData()

    return (
      <CommonLayout pageName={PAGE_NAME.search}>
        <style jsx>{styles}</style>
        <SearchContent
          filterableCategories={filterableCategories}
          isPhone={isPhone}
          query={query}
          results={results}
          sortAndFilterData={sortAndFilterData}
          t={t}
          track={this.track}
          onChangeURL={this.onChangeURL}
          sortBy={sortQuery}
        />
      </CommonLayout>
    )
  }
}

Search.defaultProps = {
  results: null,
}

Search.propTypes = {
  filter: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isPhone: PropTypes.bool.isRequired,
  query: PropTypes.string.isRequired,
  results: PropTypes.shape({
    products: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        id_product: PropTypes.number,
      }),
    ),
    totalHits: PropTypes.number,
  }),
  sort: PropTypes.shape({}).isRequired,
  t: PropTypes.func.isRequired,
}

const Extended = withI18next()(Search)

export { Search }

export default connect(
  /* istanbul ignore next */
  (state) => ({
    filter: state.filter,
    internationalization: state.internationalization,
    isPhone: state.device.isPhone,
    sort: state.sort,
  }),
)(Extended)
